import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { get } from "lodash";
import Link from "../components/Link";
import PrismicRichText from "../components/PrismicRichText";
import * as colors from "../constants/colors";
import { RichText } from "prismic-reactjs";
import callFilledIcon from "../images/icons/call-filled.svg";
import StoreHours from "../components/StoreHours";

const StlRoot = styled.section`
  padding: 28px 20px;
  color: ${colors.textDarkest};

  @media (min-width: 900px) {
    padding: 56px;
    margin-bottom: 80px;
  }
`;

const StlInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const StlGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: 48px;
    align-items: start;
  }
`;

const StlSliderWrap = styled.div`
  width: 100%;
`;

const StlMainSlide = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: ${colors.gray1};
  width: 100%;
  min-height: 250px;

  @media (min-width: 900px) {
    min-height: 510px;
  }
`;

const StlStoreHours = styled(StoreHours)`
  padding-bottom: 20px;
`;

const StlMainImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  object-position: center;

  @media (min-width: 900px) {
    height: 510px;
  }
`;

const StlSliderControls = styled.div`
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 20px;
  padding: 6px 10px;
`;

const StlControlButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.white};
  width: 18px;
  height: 18px;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
`;

const StlSlideCount = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 74px;
  border-radius: 999px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.2);
`;

const StlSlideDot = styled.span`
  display: block;
  height: 11px;
  width: ${(props) => (props.active ? "32px" : "11px")};
  border-radius: 999px;
  background: ${(props) =>
    props.active ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.6)"};
  transition:
    width 0.2s ease,
    background-color 0.2s ease;
`;

const StlThumbList = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 10px;
`;

const StlThumbButton = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 6px;
  border: 1px solid
    ${(props) => (props.active ? colors.buttonOrangeDark : colors.gray2)};
  background-color: ${colors.gray1};
  background-image: ${(props) =>
    props.imageUrl ? `url(${props.imageUrl})` : "none"};
  background-size: cover;
  background-position: center;
  cursor: pointer;
`;

const StlInfoCol = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 30px;

  h1,
  h2,
  h3,
  h4,
  p {
    margin: 0;
    color: inherit;
  }
`;

const StlEyebrow = styled.div`
  color: #818181;
  @media (min-width: 900px) {
    margin-bottom: 4px;
  }
`;

const StlLocation = styled.div`
  margin-top: -6px;
  margin-bottom: 12px;
  h2 {
    margin-bottom: 16px;
  }
  @media (min-width: 900px) {
    margin-bottom: 20px;
    h2 {
      margin-bottom: 20px;
    }
    p {
      font-size: 14px;
    }
  }
`;

const StlDirectionsButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 164px;
  border-radius: 6px;
  background: ${colors.heroOrange};
  color: ${colors.white};
  font-weight: 700;
  text-decoration: none;

  @media (min-width: 900px) {
    min-height: 64px;
    min-width: 256px;
    font-size: 18px;
    margin-bottom: 18px;
  }
`;

const StlPhone = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  margin-top: 12px;
`;

const StlPhoneIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const getImageField = (item) => {
  const storeImage = get(item, "store_image.url");
  const alt = get(item, "store_image.alt");

  if (!storeImage) {
    return null;
  }

  return {
    storeImage,
    alt,
  };
};

const StoreInfo = (slice) => {
  const primary = get(slice, "primary", {});
  const items = get(slice, "items", []);
  const [activeIndex, setActiveIndex] = useState(0);

  const slides = useMemo(
    () => items.map(getImageField).filter(Boolean).slice(0, 5),
    [items],
  );

  useEffect(() => {
    if (activeIndex > slides.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex, slides.length]);

  const currentSlide = slides[activeIndex];
  const directionsText =
    get(primary, "directions_text[0].text") ||
    get(primary, "directions_text") ||
    "Get Directions";
  const directionsUrl = get(primary, "directions_link.url");
  const directionsUid = get(primary, "directions_link.uid");
  const directionsTarget = get(primary, "directions_link.target");

  const heading = get(primary, "heading");
  const addressCopy = get(primary, "store_address");
  const phone = get(primary, "phone");

  if (!currentSlide && !Object.keys(primary).length) {
    return null;
  }

  return (
    <StlRoot>
      <StlInner>
        <StlGrid>
          <StlSliderWrap>
            {currentSlide && (
              <StlMainSlide>
                <picture>
                  {currentSlide.storeImage && (
                    <source
                      media="(max-width: 899px)"
                      srcSet={currentSlide.storeImage}
                    />
                  )}
                  <StlMainImage
                    src={currentSlide.storeImage}
                    alt={currentSlide.alt}
                  />
                </picture>
                {slides.length > 1 && (
                  <StlSliderControls>
                    <StlControlButton
                      type="button"
                      aria-label="Previous image"
                      onClick={() =>
                        setActiveIndex((prev) =>
                          prev === 0 ? slides.length - 1 : prev - 1,
                        )
                      }
                    >
                      {"<"}
                    </StlControlButton>
                    <StlSlideCount aria-hidden="true">
                      {slides.map((_, index) => (
                        <StlSlideDot
                          key={`store-info-slide-dot-${index}`}
                          active={index === activeIndex}
                        />
                      ))}
                    </StlSlideCount>
                    <StlControlButton
                      type="button"
                      aria-label="Next image"
                      onClick={() =>
                        setActiveIndex((prev) =>
                          prev === slides.length - 1 ? 0 : prev + 1,
                        )
                      }
                    >
                      {">"}
                    </StlControlButton>
                  </StlSliderControls>
                )}
              </StlMainSlide>
            )}
            {slides.length > 1 && (
              <StlThumbList>
                {slides.map((slide, index) => (
                  <StlThumbButton
                    key={`store-info-thumb-${index}`}
                    type="button"
                    aria-label={`Show image ${index + 1}`}
                    imageUrl={slide.storeImage}
                    active={index === activeIndex}
                    onClick={() => setActiveIndex(index)}
                  />
                ))}
              </StlThumbList>
            )}
          </StlSliderWrap>
          <StlInfoCol>
            <StlEyebrow>
              <PrismicRichText render={heading} />
            </StlEyebrow>
            <StlLocation>
              <PrismicRichText render={addressCopy} />
            </StlLocation>

            {(directionsUrl || directionsUid) && (
              <div>
                <Link
                  to={`/${directionsUid || ""}`}
                  url={directionsUrl}
                  target={directionsTarget}
                >
                  <StlDirectionsButton>{directionsText}</StlDirectionsButton>
                </Link>
              </div>
            )}
            <StlPhone>
              <StlPhoneIcon src={callFilledIcon} alt="" aria-hidden="true" />
              <RichText render={phone} />
            </StlPhone>
            <StlStoreHours />
          </StlInfoCol>
        </StlGrid>
      </StlInner>
    </StlRoot>
  );
};

export default StoreInfo;
